import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import rightSideImage from '../../assets/images/rightside.svg';
import toast from 'react-hot-toast';
import axios from 'axios';
import apiEndpoints from '../../components/constants/apiEndpoints';
import { useAuth } from '../../components/auth/authProvider';
import Modal from '../../components/Modal/Modal';

const SignIn = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (activeTab === 'login') {
      if (!email || !password) {
        return toast.error("Please fill in all required fields.", { position: "top-center" });
      }
      
      handleLogin();
    } else {
      if (!username || !email || !password || !confirmPassword) {
        return toast.error("All fields are required.", { position: "top-center" });
      }

      if (password !== confirmPassword) {
        return toast.error("Passwords do not match.", { position: "top-center" });
      }

      handleSignup();
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(apiEndpoints.auth.login, {
        email,
        password,
      }, { withCredentials: true });

      // Store complete user object in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success("Login successful!", { position: "top-center" });
      login(response.data.user); // Pass user data to login function
      navigate("/");
    } catch (error) {
      console.error(error);
      if (error.response?.data?.message === "User is not approved") {
        setIsModalOpen(true);
        return;
      }
      toast.error(error.response?.data?.message || "Login failed!", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      await axios.post(apiEndpoints.auth.signup, {
        username,
        email,
        password,
      }, { withCredentials: true });

      setIsModalOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Registration failed!";
      toast.error(errorMessage, { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] p-2">
      <div className="flex gap-2 min-h-screen max-w-[100%]">
        {/* Left side - Image */}
        <div className="hidden lg:block lg:w-[50%] rounded-[20px] overflow-hidden">
          <img 
            src={rightSideImage} 
            alt="Dashboard Preview" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Right side - Login Form */}
        <div className="w-full lg:w-[50%] bg-white rounded-[20px] p-20 flex ">
          <div className="max-w-[420px] mx-auto w-full">
            {/* Header Section with fixed height to prevent jerk */}
            <div className="text-center min-h-[120px] flex flex-col justify-center mb-8">
              {activeTab === 'login' ? (
                <>
                  <h1 className="text-[32px] font-medium text-[rgba(0, 0, 0, 1)] mb-2">
                    Welcome Back, Again!
                  </h1>
                  <p className="text-[#9b9b9b] text-sm font-normal	">
                    Get ready to dive back into exciting opportunities, explore new features, and
                    continue your journey toward success.
                  </p>
                </>
              ) : (
                <h1 className="text-[32px] font-medium text-[rgba(0, 0, 0, 1)]">
                  Create a Free Account
                </h1>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="bg-[#F5F5F5] p-1 rounded-[100px] flex mb-8">
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 py-2 px-6 rounded-[100px] text-base font-medium transition-all ${
                  activeTab === 'signup'
                    ? 'bg-white text-[rgba(0, 0, 0, 1)] shadow-sm'
                    : 'text-[rgba(0, 0, 0, 1)]'
                }`}
                disabled={loading}
              >
                Sign Up
              </button>
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 py-2 px-6 rounded-[100px] text-base font-medium transition-all ${
                  activeTab === 'login'
                    ? 'bg-white text-[rgba(0, 0, 0, 1)] shadow-sm'
                    : 'text-[rgba(0, 0, 0, 1)]'
                }`}
                disabled={loading}
              >
                Login
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {activeTab === 'signup' && (
                <div>
                  <label htmlFor="username" className="block text-[16px] font-medium text-[rgba(0, 0, 0, 1)] mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-4 py-[14px] bg-white border border-[#DFE6E9] rounded-[100px] text-[14px] text-[rgba(0, 0, 0, 1)] placeholder-[rgba(0, 0, 0, 1)] focus:outline-none focus:border-[#8D50FE] transition-all"
                      placeholder="Enter your username"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-[16px] font-medium text-[rgba(0, 0, 0, 1)] mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-[14px] bg-white border border-[#DFE6E9] rounded-[100px] text-[14px] text-[rgba(0, 0, 0, 1)] placeholder-[rgba(0, 0, 0, 1)] focus:outline-none focus:border-[#8D50FE] transition-all"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-[16px] font-medium text-[rgba(0, 0, 0, 1)] mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-[14px] bg-white border border-[#DFE6E9] rounded-[100px] text-[14px] text-[rgba(0, 0, 0, 1)] placeholder-[rgba(0, 0, 0, 1)] focus:outline-none focus:border-[#8D50FE] transition-all"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="#939393"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z" fill="#939393"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {activeTab === 'signup' && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-[16px] font-medium text-[rgba(0, 0, 0, 1)] mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-[14px] bg-white border border-[#DFE6E9] rounded-[100px] text-[14px] text-[rgba(0, 0, 0, 1)] placeholder-[rgba(0, 0, 0, 1)] focus:outline-none focus:border-[#8D50FE] transition-all"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="#939393"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z" fill="#939393"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'login' && (
                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-[#8D50FE] text-sm hover:underline">
                    Forgot Password?
                  </Link>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-[#8D50FE] text-white py-[14px] px-4 rounded-[100px] hover:bg-[#8D50FE]/90 transition-all text-base font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {activeTab === 'login' ? 'Logging in...' : 'Signing up...'}
                  </div>
                ) : (
                  activeTab === 'login' ? 'Login' : 'Sign Up'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Registration Success Modal */}
      {isModalOpen && activeTab === 'signup' && (
        <Modal
          maxWidth="600px"
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            navigate("/login");
          }}
          center
        >
          <div className="p-6 text-center">
            <h1 className="text-2xl font-bold mb-4">Thank you for creating your account!</h1>
            <p className="mb-6">Someone from our sales team will get back with you soon.</p>
            <button
              className="bg-[#8D50FE] text-white py-2 px-6 rounded-full hover:bg-[#8D50FE]/90 transition-all"
              onClick={() => {
                setIsModalOpen(false);
                navigate("/login");
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Account Not Approved Modal */}
      {isModalOpen && activeTab === 'login' && (
        <Modal
          maxWidth="500px"
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          center
        >
          <div className="p-6 text-center">
            <h1 className="text-2xl font-bold mb-4">Account not approved!</h1>
            <p className="mb-6">Your account is not approved yet. Someone from our sales team will get back with you soon!</p>
            <button
              className="bg-[#8D50FE] text-white py-2 px-6 rounded-full hover:bg-[#8D50FE]/90 transition-all"
              onClick={() => setIsModalOpen(false)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SignIn; 